import React, { useState } from 'react';

const HowTo = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does the random name picker ensure fairness?",
      answer: "Our wheel uses a cryptographically secure random number generator to ensure true randomness. Each name has an exactly equal probability of being selected, making it completely fair and unbiased."
    },
    {
      question: "Can I use the wheel spinner for classroom activities?",
      answer: "Absolutely! Our classroom name spinner is perfect for educational settings. Teachers use it for student selection, group formation, presentation order, and participation activities. It promotes fairness and keeps students engaged."
    },
    {
      question: "Is there a limit to how many names I can add?",
      answer: "While there's no strict limit, we recommend keeping it under 50 names for optimal performance and readability. The wheel automatically adjusts segment sizes to accommodate your entries."
    },
    {
      question: "Is the wheel spinner free to use?",
      answer: "Yes, our name picker wheel is completely free! There are no hidden fees, subscriptions, or premium features. You get full access to all functionality at no cost."
    }
  ];

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        padding: '50px 20px',
        backgroundColor: 'white',
        borderRadius: '15px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{
          fontSize: '42px',
          fontWeight: 'bold',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          📖 How to Use Wheel of Names - Complete Guide
        </h1>
        <p style={{
          fontSize: '20px',
          color: '#666',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          Learn everything about our <strong>random name picker</strong> with step-by-step instructions, tips, and answers to frequently asked questions.
        </p>
      </div>

      {/* Quick Start Guide */}
      <div style={{
        backgroundColor: '#f0f8ff',
        padding: '40px',
        borderRadius: '15px',
        marginBottom: '40px',
        border: '2px solid #e0e7ff'
      }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#1e40af',
          marginBottom: '30px'
        }}>
          🚀 Quick Start Guide
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          marginBottom: '30px'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#dc2626',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              1
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#333'
            }}>
              Add Names
            </h3>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Type names in the input field and click "Add" or press Enter. Build your list of participants.
            </p>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#dc2626',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              2
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#333'
            }}>
              Spin the Wheel
            </h3>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Click the red "SPIN" button and watch the wheel rotate with exciting animation and sound effects.
            </p>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#dc2626',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              3
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#333'
            }}>
              See Results
            </h3>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Enjoy the confetti celebration as the winner is announced clearly on the screen.
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff3cd',
          padding: '20px',
          borderRadius: '10px',
          border: '2px solid #ffeeba',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#856404',
            margin: '0'
          }}>
            💡 Pro Tip: Need at least 2 names to spin the wheel. The more names you add, the more exciting it gets!
          </p>
        </div>
      </div>

      {/* Advanced Features */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '50px',
        borderRadius: '15px',
        marginBottom: '40px'
      }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#333',
          marginBottom: '40px'
        }}>
          ⚡ Advanced Features & Tips
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '25px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#dc2626',
              marginBottom: '15px'
            }}>
              🎨 Visual Customization
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '15px' }}>
              Our <strong>name wheel generator</strong> automatically assigns unique colors to each segment for easy identification.
            </p>
            <ul style={{ listStyle: 'none', padding: '0' }}>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                12 distinct color scheme rotation
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                High contrast for accessibility
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                Names displayed clearly on segments
              </li>
            </ul>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#dc2626',
              marginBottom: '15px'
            }}>
              📱 Device Compatibility
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '15px' }}>
              Our <strong>mobile spinner</strong> works perfectly across all devices and screen sizes.
            </p>
            <ul style={{ listStyle: 'none', padding: '0' }}>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                Responsive design for phones/tablets
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                Touch-friendly interface
              </li>
              <li style={{ marginBottom: '8px', paddingLeft: '20px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '0', color: '#dc2626' }}>•</span>
                Works in all modern browsers
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '50px',
        borderRadius: '15px',
        marginBottom: '40px'
      }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#333',
          marginBottom: '40px'
        }}>
          ❓ Frequently Asked Questions
        </h2>
        
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {faqs.map((faq, index) => (
            <div key={index} style={{
              marginBottom: '15px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => toggleFaq(index)}
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#333',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                {faq.question}
                <span style={{
                  fontSize: '24px',
                  color: '#dc2626',
                  transform: openFaq === index ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}>
                  +
                </span>
              </button>
              {openFaq === index && (
                <div style={{
                  padding: '0 20px 20px',
                  fontSize: '16px',
                  color: '#666',
                  lineHeight: '1.6'
                }}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        textAlign: 'center',
        padding: '50px 30px',
        backgroundColor: '#dc2626',
        color: 'white',
        borderRadius: '15px'
      }}>
        <h2 style={{
          fontSize: '36px',
          fontWeight: 'bold',
          marginBottom: '20px'
        }}>
          Ready to Start Using Our Name Picker? 🎯
        </h2>
        <p style={{
          fontSize: '20px',
          marginBottom: '30px',
          opacity: '0.95',
          maxWidth: '600px',
          margin: '0 auto 30px'
        }}>
          Now that you know how to use our <strong>free wheel spinner</strong>, it's time to experience the fairness and excitement for yourself!
        </p>
        <a 
          href="/"
          style={{
            display: 'inline-block',
            backgroundColor: 'white',
            color: '#dc2626',
            padding: '15px 35px',
            borderRadius: '30px',
            textDecoration: 'none',
            fontWeight: 'bold',
            fontSize: '20px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
          }}
        >
          Try Wheel of Names Now →
        </a>
        <div style={{
          marginTop: '20px',
          fontSize: '16px',
          opacity: '0.8'
        }}>
          No registration • No downloads • 100% Free
        </div>
      </div>
    </div>
  );
};

export default HowTo;